/**
 * Error Boundary Component for Results Pages
 *
 * Catches React errors in Results components and shows friendly message.
 * Logs error details for debugging.
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { logger } from '../../../lib/logging';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ResultsErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error with correlation ID and component stack
    logger.error('results_error_boundary_triggered', undefined, {
      op: 'results_error_boundary_triggered',
      component: 'ResultsErrorBoundary',
      correlationId: crypto.randomUUID(),
      err_code: error.name,
      err_message: error.message,
      error_stack: error.stack?.substring(0, 500), // Truncate stack
      component_stack: errorInfo.componentStack?.substring(0, 500) // Truncate component stack
    });

    this.setState({
      errorInfo
    });
  }

  handleReset = (): void => {
    logger.info('results_error_boundary_reset', undefined, {
      op: 'results_error_boundary_reset',
      component: 'ResultsErrorBoundary'
    });

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });

    // Reload the page to reset state
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-red-500/50 rounded-xl shadow-2xl max-w-lg w-full p-8 text-center">
            {/* Error Icon */}
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>

            {/* Error Message */}
            <h2 className="text-2xl font-bold text-white mb-2">
              Something went wrong
            </h2>
            <p className="text-slate-400 text-sm mb-6">
              An unexpected error occurred while loading the Results page. The error has been logged for debugging.
            </p>

            {/* Error Details (dev mode only) */}
            {import.meta.env.MODE === 'development' && this.state.error && (
              <div className="bg-slate-900/50 border border-slate-700/50 rounded-lg p-4 mb-6 text-left">
                <h3 className="text-xs font-semibold text-red-400 uppercase tracking-wider mb-2">
                  Error Details (Dev Only)
                </h3>
                <pre className="text-xs text-slate-300 overflow-x-auto whitespace-pre-wrap break-words">
                  {this.state.error.message}
                </pre>
                {this.state.error.stack && (
                  <pre className="text-xs text-slate-500 overflow-x-auto whitespace-pre-wrap break-words mt-2">
                    {this.state.error.stack.substring(0, 300)}...
                  </pre>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-center space-x-3">
              <button
                onClick={this.handleReset}
                className="px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-sm font-medium text-white transition-colors"
              >
                Try again
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className="px-4 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-sm font-medium text-white transition-colors"
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
